import { useEffect } from 'react';
import { useRecoilValue } from 'recoil';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { useResidentEnvironments } from '~/hooks/environment/useResidentEnvironments';
import useWorkflowSteps from '~/containers/viewer/explorer-sidebar/WorkflowStepper/useWorkflowSteps';
import { AdjacentSliceGhostDepths } from '~/engine/core/render/renderers/slices/adjacentSliceGhost';
import {
    adjacentSliceGhostDepthsState,
    adjacentSliceGhostsEnabledState,
} from '~/state/atoms/viewerAtoms';

/** The workflow steps on which the adjacent-slice ghosts are shown. */
const GHOST_STEP_IDS: ReadonlySet<string> = new Set(['contouring', 'review']);

/**
 * The slices on each side whose contours the viewports ghost, or null while
 * the display is switched off or the current workflow step is not one of
 * {@link GHOST_STEP_IDS}. The switch and choice of slices are kept either
 * way, so the ghosts return as they were on coming back to such a step.
 */
export function useShownAdjacentSliceGhosts(): AdjacentSliceGhostDepths | null {
    const enabled = useRecoilValue(adjacentSliceGhostsEnabledState);
    const depths = useRecoilValue(adjacentSliceGhostDepthsState);
    const { currentStep } = useWorkflowSteps();
    const onGhostStep = !!currentStep && GHOST_STEP_IDS.has(currentStep.id);
    return enabled && onGhostStep ? depths : null;
}

/**
 * Keeps the engine's adjacent-slice ghosts in step with
 * {@link useShownAdjacentSliceGhosts} on the Environment of the active
 * structure set, and off on every other resident Environment, so no row of
 * another image draws them. An Environment minted for the active set later
 * gets the current setting. Each Environment is written only when its
 * setting differs, since a write repaints its contour layer. Mount once in
 * an always-rendered viewer component.
 */
export function useAdjacentSliceGhosts(): void {
    const shown = useShownAdjacentSliceGhosts();
    const { environment: target } = useActiveEditTarget();
    const residents = useResidentEnvironments();

    useEffect(() => {
        for (const environment of residents) {
            const depths = environment === target ? shown : null;
            if (environment.adjacentSliceGhosts !== depths) {
                environment.updateAdjacentSliceGhosts(depths);
            }
        }
    }, [residents, target, shown]);
}
